export interface CoastLine {
  // Lake shoreline points (the water boundary)
  lakePoints: { x: number; y: number }[];
  // Outer land boundary (for filling)
  outerPoints: { x: number; y: number }[];
  // Lake boundaries for collision detection
  bounds: {
    left: number;
    right: number;
    top: number;
    bottom: number;
  };
  // Lake center and size for minimap
  center: { x: number; y: number };
  radiusX: number;
  radiusY: number;
}

// Seeded random for consistent coastline generation
function seededRandom(seed: number): () => number {
  return () => {
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280;
  };
}

// Multi-octave noise for natural coastline variation
function coastNoise(t: number, seed: number, amplitude: number, octaves: number = 4): number {
  let value = 0;
  let amp = amplitude;
  let freq = 1;

  for (let i = 0; i < octaves; i++) {
    value += Math.sin(t * freq * 3 + seed) * amp;
    value += Math.cos(t * freq * 5 + seed * 1.3) * amp * 0.7;
    amp *= 0.5;
    freq *= 2.1;
  }

  return value;
}

export function generateCoastLine(width: number, height: number): CoastLine {
  const random = seededRandom(42);

  // Lake center and base dimensions - large lake
  const centerX = width * 0.5;
  const centerY = height * 0.5;
  const baseRadiusX = width * 0.9;  // Wide lake
  const baseRadiusY = height * 0.9; // Tall lake

  // Generate lake shoreline as an irregular ellipse
  const lakePoints: { x: number; y: number }[] = [];
  const segments = 120; // More segments for smooth shoreline

  for (let i = 0; i < segments; i++) {
    const t = i / segments;
    const angle = t * Math.PI * 2;

    // Base ellipse position
    const baseX = Math.cos(angle) * baseRadiusX;
    const baseY = Math.sin(angle) * baseRadiusY;

    // Add natural variation to the shoreline
    const largeFeature = coastNoise(t, 1.5, 50, 3);   // Large bays and peninsulas
    const mediumFeature = coastNoise(t * 3, 2.7, 25, 2); // Coves
    const smallDetail = coastNoise(t * 7, 4.2, 10, 2);  // Rocky details
    const beachVariation = Math.sin(t * 30 + random() * 0.5) * 12 * (0.5 + random() * 0.5);

    // Apply noise perpendicular to the shore (radially)
    const noise = largeFeature + mediumFeature + smallDetail + beachVariation;
    const noiseX = Math.cos(angle) * noise;
    const noiseY = Math.sin(angle) * noise;

    lakePoints.push({
      x: centerX + baseX + noiseX,
      y: centerY + baseY + noiseY,
    });
  }

  // Calculate actual bounds from the generated points
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const p of lakePoints) {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y);
  }

  // Outer boundary for land (extends beyond visible area)
  const extension = 3000;
  const outerPoints = [
    { x: minX - extension, y: minY - extension },
    { x: maxX + extension, y: minY - extension },
    { x: maxX + extension, y: maxY + extension },
    { x: minX - extension, y: maxY + extension },
  ];

  return {
    lakePoints,
    outerPoints,
    bounds: {
      left: minX + 40,    // Add margin for collision
      right: maxX - 40,
      top: minY + 40,
      bottom: maxY - 40,
    },
    center: { x: centerX, y: centerY },
    radiusX: baseRadiusX,
    radiusY: baseRadiusY,
  };
}

export function renderCoast(
  ctx: CanvasRenderingContext2D,
  coast: CoastLine,
  isDarkMode: boolean
) {
  // This function is kept for compatibility but lake rendering is done in renderer.ts
  ctx.save();
  ctx.restore();
}

// Check if a point is on land (outside the lake)
export function isOnLand(
  x: number,
  y: number,
  coast: CoastLine | null,
  width: number,
  height: number
): boolean {
  if (!coast) {
    // Fallback if no coast
    return false;
  }

  // Check if point is outside the lake bounds
  return x < coast.bounds.left ||
         x > coast.bounds.right ||
         y < coast.bounds.top ||
         y > coast.bounds.bottom;
}

// Get the nearest valid water position (inside the lake)
export function constrainToWater(
  x: number,
  y: number,
  width: number,
  height: number,
  margin: number = 20,
  coast: CoastLine | null = null
): { x: number; y: number; hitLand: boolean } {
  if (!coast) {
    return { x, y, hitLand: false };
  }

  let newX = x;
  let newY = y;
  let hitLand = false;

  // Constrain to lake bounds (keep inside the lake)
  if (x < coast.bounds.left + margin) {
    newX = coast.bounds.left + margin;
    hitLand = true;
  } else if (x > coast.bounds.right - margin) {
    newX = coast.bounds.right - margin;
    hitLand = true;
  }

  if (y < coast.bounds.top + margin) {
    newY = coast.bounds.top + margin;
    hitLand = true;
  } else if (y > coast.bounds.bottom - margin) {
    newY = coast.bounds.bottom - margin;
    hitLand = true;
  }

  return { x: newX, y: newY, hitLand };
}
